import { supabase } from './supabase';

export interface InkooporderInput {
  parentAccountId: string;
  inkoopordernummer: string;
  locatiecode: string;
  vestigingsnaam: string;
  straatHuisnummer: string;
  postcode: string;
  plaats: string;
  managerNaam?: string;
  managerEmail?: string;
  managerTelefoon?: string;
  aantalKleden: number;
  gewensteAfleverDatum?: string;        // ISO yyyy-mm-dd
  inkoopordertotaalText?: string;       // bv. '€500,00 incl. BTW'
}

export interface InkooporderResult {
  workOrderId: string;
  workOrderNumber: string;
  contactId: string;
  contactCreated: boolean;
}

export class InkooporderError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
  }
}

export interface LocatieMatch {
  id: string;
  name: string;
  email: string;
  phone: string;
  matchedLocatiecode: string;
}

async function getToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

export async function submitInkooporder(input: InkooporderInput): Promise<InkooporderResult> {
  const token = await getToken();
  if (!token) throw new InkooporderError('AUTH', 'Niet ingelogd');

  let res: Response;
  try {
    res = await fetch('/api/orders/create-b2b-inkooporder', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(input),
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    throw new InkooporderError('NETWORK', msg);
  }

  let data: {
    success?: boolean;
    errorCode?: string;
    errorMessage?: string;
    workOrderId?: string;
    workOrderNumber?: string;
    contactId?: string;
    contactCreated?: boolean;
    error?: string;
    code?: string;
  };
  try {
    data = await res.json();
  } catch {
    throw new InkooporderError('PARSE', `Server respons ongeldig (status ${res.status})`);
  }

  if (!res.ok || !data.success) {
    throw new InkooporderError(
      data.errorCode || data.code || 'UNKNOWN',
      data.errorMessage || data.error || 'Aanmaken mislukt',
    );
  }

  return {
    workOrderId: data.workOrderId!,
    workOrderNumber: data.workOrderNumber!,
    contactId: data.contactId!,
    contactCreated: !!data.contactCreated,
  };
}

export async function findContactByLocatiecode(
  accountId: string,
  locatiecode: string,
): Promise<LocatieMatch | null> {
  const token = await getToken();
  if (!token) return null;

  const url = `/api/customers/contacts-by-account?id=${encodeURIComponent(accountId)}&lastName=${encodeURIComponent(locatiecode)}`;
  let res: Response;
  try {
    res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  } catch {
    return null;
  }
  if (!res.ok) return null;

  const data = await res.json().catch(() => ({ contacts: [] }));
  const contacts = (data.contacts || []) as Array<{ id?: string; name?: string; email?: string; phone?: string }>;
  const first = contacts[0];
  if (!first?.id) return null;

  return {
    id: first.id,
    name: first.name ?? '',
    email: first.email ?? '',
    phone: first.phone ?? '',
    matchedLocatiecode: locatiecode,
  };
}

const LOCATIECODE_REGEX = /^[A-Za-z0-9-]{3,12}$/;

export function isLocatiecodeFormat(value: string): boolean {
  return LOCATIECODE_REGEX.test(value);
}
